import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Popover, PopoverBody } from 'reactstrap'
import { Link } from 'react-router-dom'
import { DateTime } from 'luxon'
import introJs from 'intro.js'
import $ from 'jquery'

import HierarchySelection from '../Layout/HierarchySelection'

import KpiItem from '../Layout/Common/KpiItem'
import WebsiteScoreKpi from '../Kpi/PageSpeed/WebsiteScoreKpi'
import TaskListCard from './TaskListCard'

import { getEcomMetrics, getMonthlyMetricsLongTerm, getPageSpeedMetrics, processEcomMetrics, processMonthlyMetric } from '../../utils/Metrics'
import { DATATABLE_DEFAULT_OPTIONS, formatAverage, formatPercent, polarAreaGraph, socialChannels } from '../../utils/common'
import { updateUser } from '../../utils/Users'
import { getScoreStatsForAudit } from '../../utils/Audits'

import { getMetrics } from '../../../features/metric/metricSlice'
import processor from '../../../features/metric/processor'
import { getRecommendations } from '../../utils/Recommendations'
import { getAuditsBySite } from '../../utils/Audits'
import { populateUser } from '../../../features/session/sessionSlice'
import { getDailyLeadsBySite, processDailyLeads } from '../../utils/Leads'
import ApexChart from '../ApexCharts/ApexChart'

import USAMap from '../Map/USAMap'
import SkeletonKpi from '../Kpi/SkeletonKpi'
import SkeletonPieChart from '../Layout/Loaders/Charts/SkeletonPieChart'
import Skeleton from 'react-loading-skeleton'
import SkeletonAuditScore from '../Kpi/Audit/SkeletonAuditScore'
import SkeletonTable from '../Layout/Loaders/SkeletonTable'
import SkeletonRevenueTracker from '../Kpi/SkeletonRevenueTracker'
import { loadable } from '../Layout/Common/Loadable'
import SkeletonTaskList from '../Layout/Loaders/Overview/SkeletonTaskList'

const AuditScoreKpi = loadable(() => import('../Kpi/Audit/AuditScoreKpi'))
const RevenueTrackerCard = loadable(() => import('./RevenueTrackerCard'))

const INITIAL_STATE = {
    kpiItems: [],
    kpiItemsFb: [],
    cityMetrics: [],
    sessionKpi: {},
    revenueKpi: {},
    pageLoadKpi: {},
    billboardKpiAds: {},
    consumerSourcesSessions: [],
    DTOptionsBuilder: null,
    dtOptions: null,
    metricsLoaded: false,
    metricsLoading: false,
    popoverOpen: false,
    prevStorage: null,
    prevPageMetrics: null,
    pageMetrics: null,
    introJsProcess: null,
    firstExit: false,
    introJsInit: true,
    lastMonthsAudit: null,
    cityDictionary: {},
    cityMarkers: [],
    topCityMarkers: [],
    radialGraph: {
        series: [0],
        options: {
            chart: {
                height: 250,
                width: 250,
                type: 'radialBar'
            },
            plotOptions: {
                radialBar: {
                    startAngle: -90,
                    endAngle: 90,
                    track: {
                        background: "#e7e7e7",
                        strokeWidth: '97%',
                        margin: 5, // margin is in pixels
                        dropShadow: {
                            enabled: true,
                            top: 2,
                            left: 0,
                            color: '#999',
                            opacity: 1,
                            blur: 2
                        }
                    },
                    dataLabels: {
                        name: {
                            fontSize: '16px',
                            offsetY: 120
                        },
                        value: {
                            offsetY: 76,
                            fontSize: '22px',
                            color: undefined,
                            formatter: function (val) {
                                return val + "%";
                            }
                        }
                    }
                }
            },
            grid: {
                padding: {
                  top: -10
                }
            },
            fill: {
                colors: ["#25E427"]
            },
            labels: ['of Total Revenue']
        }
    }
}

function Overview(){

    const dispatch = useDispatch()

    const [state, setState] = useState(INITIAL_STATE)
    const model = useSelector(state => state.site.model)

    const user = useSelector(state => state.session.user)
    const isAdmin = useSelector(state => state.session.isAdmin)
    const trialMode = useSelector(state => state.session.trialMode)
    const useDarkMode = useSelector(state => state.session.useDarkMode)
    const useInteractiveGuideline = useSelector(state => state.session.useInteractiveGuideline)

    useEffect(() => {
            
        return () => {
            introJs().exit(true)
        }
    }, [])

    useEffect(() => {
        if(state.metricsLoaded){
            if($.fn.dataTable.isDataTable(".dtTblOverview")){
                $(".dtTblOverview").DataTable().destroy()
                $(".dtTblOverview").dataTable({
                    ...DATATABLE_DEFAULT_OPTIONS,
                    order: [[2, 'desc']],
                    buttons: [],
                    paging: true,
                    scrollY: '190px',
                    bLengthChange: false,
                    pagingType: "simple",
                    destroy: true
                })
                if(trialMode){
                    $(".dtTblOverview").DataTable().buttons().disable()
                    $(".dt-buttons.btn-group button").prop('disabled', true)
                }
            } else {
                $(".dtTblOverview").dataTable({
                    ...DATATABLE_DEFAULT_OPTIONS,
                    order: [[2, 'desc']],
                    buttons: [],
                    paging: true,
                    scrollY: '190px',
                    bLengthChange: false,
                    pagingType: "simple",
                    destroy: true
                })
                if(trialMode){
                    $(".dtTblOverview").DataTable().buttons().disable()
                    $(".dt-buttons.btn-group button").prop('disabled', true)
                }
            }
            $("[data-toggle='popover']").popover()


            if(user.enableIntroTutorial && state.introJsInit){
                runIntroTutorial()

                setState(prev => ({
                    ...prev,
                    introJsInit: false
                }))
            }
        }        
    }, [state, user])

    const runIntroTutorial = (timeout = 2500) => {
        const introJsProcess = introJs()

        let steps = [{
            element: document.querySelector('.periods-select'),
            intro: "Use drop down to select your choice of the last six months.",
            position: 'bottom'
        }, {
            element: document.querySelector('.site-select'),
            intro: "Multiple sites or multiple shopping carts? No problem: all you need is a dedicated Google Analytics account.",
            position: 'bottom'
        }, {
            element: document.querySelector('#notificationBell'),
            intro: "In addition to receiving email/sms alerts, your notification center will display a blue circle signifying issues related to website or campaign performance.",
            position: 'bottom'
        }, {
            element: document.querySelector('#navSidebarSection'),
            intro: "Use the tabbed navigation to select from various tabs of detailed metrics. Each tab is an important set of metrics geared toward a particular aspect of your analytics strategy.",
            position: 'right'
        }, {
            element: document.querySelector('#auditTab'),
            intro: "Our Audits and Insights automatically identifies performance issues with respect to your website, email, social, or campaign performance.",
            position: 'right'
        }, {
            element: document.querySelector('#coverYourSix'),
            intro: "This report outlines goals or transactions from the top six channels your competitors use. It quickly identifies missed opportunities.",
            position: 'right'
        }, {
            element: document.querySelector('#cityMap'),
            intro: "Instantly see where your brand has conversions or transactions from by city to identify opportunity for expansion & growth. ",
            position: 'left'
        }, {
            element: document.querySelector('#scoreCardBtn'),
            intro: "Score Card is a new feature that is an executive overview of all top KPI's important to your business.",
            position: 'bottom'
        }, {
            element: document.querySelector('#revenueTrackerContainer'),
            intro: "Revenue Tracker is designed to help you set & monitor your sales & revenue goals for this year and also compares to last year.",
            position: 'left'
        }, {
            element: document.querySelector('#claimServiceBtn'),
            intro: "If any tab is missing data, access your site admin screen here to claim any missing channels like Google Business Profile, Facebook Ads, etc.",
            position: 'bottom'
        }]

        if(trialMode){
            steps.splice(4, 0, {
                element: document.querySelector('#startupBtn'),
                intro: "Don't forgot to complete your startup checklist to get the full power of your dashboard!",
                position: 'right'
            })
        }

        introJsProcess.setOptions({
            scrollToElement: false,
            steps
        })
        setTimeout(() => {
            introJsProcess.start()
            setState(prevState => ({
                ...prevState,
                introJsProcess
            }))
        }, timeout)
    }

    const onIntoJsExit = (trueExit = false) => async() => {
        if(!state.firstExit && !isAdmin){
            if(trueExit){
                await updateUser(localStorage.token, {...user, enableIntroTutorial: false})

                dispatch(populateUser({
					...user,
					enableIntroTutorial: false
				}))
            }
            setState(prevSrate => ({
                ...prevSrate,
                firstExit: true
            }))
        }
        else if(state.firstExit && user.enableIntroTutorial){

            await updateUser(localStorage.token, {...user, enableIntroTutorial: false})
        }
    }
    
    const onSelection = async() => { 
        const now = DateTime.local()
        const currentYear = now.toFormat("yyyy")

        let site = model.sites[model.siteActiveIdx] ? model.sites[model.siteActiveIdx] : model.sites[0]
        let startDate = model.periods[0].startISO
        let endDate = model.periods[0].endISO

        let dataPath = `${site.siteId}/${startDate}/${endDate}`
                
        let apiPulls = [dispatch(getMetrics({key: dataPath, nocache: false, override: true}))
            .then(async(res) => {
                if(res?.payload?.error){
                    throw res?.payload?.error
                }
                const processedData = await processor(res.payload.current.data, dataPath, site)

                return {
                    ...res.payload.current,
                    ...processedData
                }
            }), 
            getPageSpeedMetrics(`${dataPath}`),
            getEcomMetrics(site.siteId, startDate, endDate).then(async(m) => {
                if(m === undefined){
                    throw new Error("Error")
                }
                const ecom = await processEcomMetrics(m)

                let thisMonth = m.filter(metric => DateTime.fromISO(metric.date) >= now.startOf("month"))
                let thisMonthEcom = await processEcomMetrics(thisMonth)
                return {
                    ...ecom,
                    thisMonthEcom
                }
            })
        ]
        
        // if((parseInt(model.period) + 1) < model.periods.length){
            let startISO = model.periods[1].startISO
            let endISO = model.periods[1].endISO
            let key = `${site.siteId}/${startISO}/${endISO}`

            apiPulls.push(dispatch(getMetrics({key, nocache: false, override: true}))
            .then(async(res) => {
                const processedData = await processor(res.payload.current.data, key, site)

                return {
                    ...res.payload.current,
                    ...processedData
                }
            }))
            
            apiPulls.push(getPageSpeedMetrics(`${key}`).then(({response}) => response.result))

            apiPulls.push(
                getEcomMetrics(site.siteId, startISO, endISO).then(m => {
                    if(m === undefined){
                        throw new Error("Error")
                    }
                    return processEcomMetrics(m)
                }))
        // } else {
        //     apiPulls.push(Promise.resolve({}))
        //     apiPulls.push(Promise.resolve([]))
        //     apiPulls.push(Promise.resolve({}))
        // }

        let recomISO = model.periods[1].startISO

        let yyyyMM = DateTime.fromISO(recomISO).toFormat("yyyyMM")

        apiPulls.push(getRecommendations(site.siteId, yyyyMM))

        let mSDate = (now.startOf("month") > DateTime.fromISO(endDate) ? now.minus({months: 1}).startOf("month") : now.startOf("month")).toISODate()
        if(site?.attr?.isEcommerce){
            apiPulls.push(dispatch(getMetrics({key:`${site.siteId}/${mSDate}/${endDate}`, nocache: false, override: true}))
            .then(async(res) => {

                if(res?.payload?.error){
                    throw res?.payload?.error
                }
                return processor(res.payload.current.data, `${site.siteId}/${mSDate}/${endDate}`, site)
            }))
        } else {
            apiPulls.push(getDailyLeadsBySite(site.siteId, mSDate, endDate)
                .then(({response, error}) => response ? processDailyLeads(response.result, mSDate, endDate) : {}))
        }

        if(site?.attr?.auditBaseline){
            apiPulls.push(getAuditsBySite(null, site.siteId, null, yyyyMM)
            .then(({response, error}) => {
                if(error){
                    return null
                } else {
                    if(response.audits.length > 0){
                        return response.audits[0]
                    }
                    
                    return null
                }
            }))
        } else {
            apiPulls.push(Promise.resolve({}))
        }

        const [
            mainMetrics, 
            { response, err }, 
            ecomMetrics, 
            prevStorage, 
            prevPageMetrics, 
            prevEcomMetrics, 
            recommendation,
            metricsThisMonth,
            lastMonthsAudit
        ] = await Promise.all(apiPulls)

        const months = await getMonthlyMetricsLongTerm(site.siteId, currentYear, currentYear)

        const monthMetrics = await Promise.all(months.response.result.map(r => processMonthlyMetric(r, true)))

        let metrics = {
            prevStorage,
            prevPageMetrics,
            mainMetrics,
            ecomMetrics,
            prevEcomMetrics,
            recommendations: recommendation?.response?.result ?? [],
            monthMetrics,
            metricsThisMonth,
            lastMonthsAudit
        }

        if(err){
            console.log(err)
        }
        if(response){
            metrics.pageMetrics = response.result
        }

        return metrics
    }
    
    const onSelectionComplete = async(metrics) => {
        let {  kpiItems, kpiItemsFb, sessionKpi, billboardKpiAds, revenueKpi, pageLoadKpi } = state
        const { mainMetrics, pageMetrics, prevStorage, prevPageMetrics, ecomMetrics, prevEcomMetrics, recommendations, monthMetrics, 
            metricsThisMonth,
            lastMonthsAudit
         } = metrics

        const { 
            gaEcomRevenue,
            gaEcomAvgOrderVal,
            gaEcomTransactions,
            thisMonthEcom
        } = ecomMetrics

        const currMetrics = {...mainMetrics}
        
        let site = model.sites[model.siteActiveIdx] ? model.sites[model.siteActiveIdx] : model.sites[0]
        
        const sessionTotals = mainMetrics.sesBySrcMediumDetails
        const { totalSessions, goals, goalConversion } = mainMetrics

        let totalRevenue = gaEcomRevenue || 0
        let transactions = gaEcomTransactions || 0

        // if(mainMetrics?.adsFBByCompaignTotal?.length > 0){        
        //     totalRevenue += (mainMetrics?.adsFBByCompaignTotal[8] || 0)
        //     transactions += (mainMetrics?.adsFBByCompaignTotal[9] || mainMetrics?.adsFBByCompaignTotal[11] || 0)
        // }

        let avgOrderVal = totalRevenue > 0 && transactions > 0 ? parseFloat((totalRevenue / transactions).toFixed(2)) : 0
        
        sessionKpi = { title: "SESSIONS", value: totalSessions || 0, prevValue: 0, 
            disableImage: true,
            localeFormatted: true,
            visualComponent: <Link className="btn btn-sm btn-info float-right" to="/channel-performance">More</Link>,
            secondaryStats: [
                { title: "Goals", value: currMetrics?.goals, color: "bg-teal"},
                { id: "gcr", title: "GCR", value: currMetrics?.goalConversion, color: "bg-blue", suffix:"%" },
            ], plottedItems: [], description: "A session is defined as a group of interactions one user takes within a 30 minute time frame on your website." 
        }

        revenueKpi = { title: "REVENUE", value: totalRevenue, prevValue: 0, prefix: "$", 
            description: "A combined revenue total provided through Google Analytics only",
            localeFormatted: true,
            visualComponent: <Link className="btn btn-sm btn-info float-right" to="/ads-ecommerce">More</Link>,
            secondaryStats: [
                { title: "AOV", value: avgOrderVal, color: "bg-teal", prefix: "$", type: "compare", prevValue: 0 },
                { title: "Transactions", value: transactions, color: "bg-blue", type: "compare", prevValue: 0 },
            ], plottedItems: [{
                data: ecomMetrics?.gaEcomMetricsDetails[0].values.map(s => ({
                    x: DateTime.fromMillis(s[0]).toFormat("LL-dd"),
                    y: s[1]
                }))
            }], description: "Revenue" 
        }
        
        let leadTotal = sessionTotals ? sessionTotals.reduce((sum, source) => {
            sum.all += source.goals

            source.daily.forEach((item,idx) => {
                let dt = DateTime.fromISO(item[0]).toFormat("LL-dd")
                if(sum.dailySessions[idx] === undefined){
                    sum.dailySessions[idx] = {x: dt, y: 0}
                }
                sum.dailySessions[idx].y += item[1]
            })
            return sum
        }, {
            all: 0,
            dailySessions: []
        }) : {
            all: 0,
            dailySessions: []
        }

        sessionKpi.plottedItems = [{
            data: leadTotal.dailySessions
        }]

        let totalAdsSpend = 0;
        let totalAdsGCR = 0
        let totalAdsGoals = 0
        let totalLeadAdSpend = 0
        let cplDivide = 0;
        let cpc = 0;

        let brandReferrals = { 
            sessions: 0, 
            leads: 0, 
            leadsPercentOverall: 0, 
            gcrAvgRate: 0, 
            gcr: 0, 
            leadsPercentByDay: [],
            dailySessions: [], 
            dailyLeads: [] 
        }
        
        if(site?.attr?.consumerSources){
            site.attr?.consumerSources.map(source => {
                let element = mainMetrics?.sesBySrcMediumDetails && mainMetrics?.sesBySrcMediumDetails.find(e => {
                    return e.id === source
                })
                let ele = { id: source, sessions:0 }

                if(element){
                    Object.assign(ele, element)
                    brandReferrals.sessions += element.sessions
                    brandReferrals.leads += element.goals
                    brandReferrals.gcr += element.goals > 0 && element.sessions > 0 ? (element.goals / element.sessions * 100) : 0
                }

            })
            brandReferrals.gcrAvgRate = (brandReferrals.gcr / site.attr?.consumerSources.length).toFixed(2)
            brandReferrals.leadsPercentOverall = (brandReferrals.leads / leadTotal.all * 100).toFixed(2)
            
        }

        if(mainMetrics && Object.keys(mainMetrics).length > 0){

            totalAdsGCR = formatPercent((mainMetrics.adsByCompaignTotal[5] || 0)+
                (mainMetrics.adsFBByCompaignTotal[5] || 0)+
                (mainMetrics.bingCampaignMetricsTotals[7] || 0) + 
                mainMetrics.linkedInGoals, ((mainMetrics.adsByCompaignTotal[0] || 0)+
                (mainMetrics.adsFBByCompaignTotal[0] || 0) + 
                (mainMetrics.bingCampaignMetricsTotals[4] || 0) +
                mainMetrics.linkedInClicks
            ) ||1, true)

            totalAdsGoals = (mainMetrics.adsByCompaignTotal[5] || 0)+ 
                (mainMetrics.adsFBByCompaignTotal[5] || 0)+ 
                (mainMetrics.bingCampaignMetricsTotals[7] || 0) +
                mainMetrics.linkedInGoals
        }
            
        if(mainMetrics.adsByCompaignTotal.length > 0){
            totalAdsSpend += mainMetrics.adsByCompaignTotal[3]
            totalLeadAdSpend += mainMetrics.adsByCompaignTotal[3]
            cplDivide += mainMetrics.adsByCompaignTotal[5]
            cpc += mainMetrics.adsByCompaignTotal[2]
        }
        if(mainMetrics.adsFBByCompaignTotal.length > 0){
            totalAdsSpend += mainMetrics.adsFBByCompaignTotal[3]
            totalLeadAdSpend += mainMetrics.adsFBByCompaignTotal[7]
            cplDivide += mainMetrics.adsFBByCompaignTotal[6]
            cpc += mainMetrics.adsFBByCompaignTotal[2]
        }
        if(mainMetrics.bingCampaignMetricsTotals.length > 0){
            totalAdsSpend += mainMetrics.bingCampaignMetricsTotals[5]
            totalLeadAdSpend += mainMetrics.bingCampaignMetricsTotals[5]
            cplDivide += mainMetrics.bingCampaignMetricsTotals[7]
            cpc += mainMetrics.bingCampaignMetricsTotals[4]
        }

        totalAdsSpend += mainMetrics.linkedInAdSpend
        totalLeadAdSpend += mainMetrics.linkedInLeadAdSpend
        cpc += mainMetrics.linkedInClicks
        cplDivide += mainMetrics.linkedInLeads

        let adSpend = parseFloat(formatAverage(Math.round(totalAdsSpend), 1)||0)
        let costPerLead = cplDivide > 0 ? (totalLeadAdSpend / cplDivide||1).toFixed(2) : 0

        let totalRevenueGMB = 0,
            gmbTransactions = 0,
            socialRevenue = 0,
            socialTransactions = 0,
            emailRevenue = 0,
            emailTransactions = 0,
            totalRevenueGA = ecomMetrics?.gaEcomRevenue || 0,
            emailGoals = 0,
            gmbGoals = 0,
            filter = ['googlemybusiness', 'googlemyprofile', 'google-my-place']
        
        let paidAdsRevenue = 0, paidAdsTransactions = 0
        let goalsFromSMM = 0
        
        let str = []
        for (let key in socialChannels) {
            str = [...str, ...socialChannels[key]]
        }
        let rex = new RegExp(`.*(${str.join('|')}).*referral$`, 'i')

        if(site.attr?.isEcommerce){
            if(ecomMetrics && ecomMetrics?.adsByCompaignTotal?.length > 0){
                paidAdsTransactions += (ecomMetrics.adsByCompaignTotal[4] || 0)
                paidAdsRevenue = (ecomMetrics.adsByCompaignTotal[6] || 0)
            }
            if(currMetrics && Object.keys(currMetrics).length > 0){
                paidAdsRevenue += (currMetrics.adsFBByCompaignTotal[8] || 0) 
                paidAdsTransactions += (currMetrics?.adsFBByCompaignTotal[9] || currMetrics?.adsFBByCompaignTotal[11] || 0)    
            }

            if(ecomMetrics && ecomMetrics?.gaEcomCampaignMetricsByChannel) {
                paidAdsRevenue += (ecomMetrics?.gaEcomCampaignMetricsByChannel?.bingCpc?.campaignTotals[4] || 0)
                paidAdsTransactions += (ecomMetrics?.gaEcomCampaignMetricsByChannel?.bingCpc?.campaignTotals[1] || 0)
            }

            ecomMetrics?.gaEcomSrcMedMetrics?.forEach(row => {
                let found = false

                filter.forEach(f => found = row[0].includes(f))
                if (found) {
                    totalRevenueGMB += row[1][4]
                    gmbTransactions += row[1][1]
                }

                if (rex.test(row[0])) {
                    for (let key in socialChannels) {
                        if (new RegExp(`.*(${socialChannels[key].join('|')}).*referral$`, 'i').test(row[0])) {
                            socialRevenue += row[1][4]
                            socialTransactions += row[1][1]
                        }
                    }
                }
            })

            if(ecomMetrics && ecomMetrics?.adsByCompaignTotal?.length > 0){
                emailRevenue = (ecomMetrics.emailEcomRevenue || 0)
                emailTransactions = ecomMetrics?.emailEcomTransactions || 0
            }
        }

        billboardKpiAds = { title: "AD SPEND", prefix: "$", value: adSpend, prevValue: 0,
            description: "A combined ad spend that includes any combination of paid ads from Google, Facebook, Linked In, or Microsoft/Bing.",
            visualComponent: <Link className="btn btn-sm btn-info float-right" to="/ads-paid">More</Link>,
            secondaryStats: [
                site.attr.isEcommerce ? {
                    title: "ROAS", value: formatAverage(paidAdsRevenue, adSpend, true), color: "bg-teal"
                } : { title: "Goals", value: (currMetrics?.adsByCompaignTotal ? currMetrics?.adsByCompaignTotal[5] || 0 : 0), color: "bg-teal"},
                { id: "cpl", title: "Cost per Lead", value: costPerLead, color: "bg-blue", prefix: "$" },
            ], 
            plottedItems: [{
                data: currMetrics?.adMetricsByDay.map(ad => ({
                    x: DateTime.fromISO(ad[0]).toFormat("LL-dd"),
                    y: ad[1][0].toFixed(2)
                }))
            }], description: "Defined as a combined daily ad spend for a given period when using Google Ads and/or Facebook Ads." 
        }
        
        let smm_data = {}, smm_data_old = {}

        let sesFromSMM = 0
        let sesFromSMMOld = 0

        let lifetimeFans = 0
        let newFans = 0

        mainMetrics.sesBySrcMediumDetails.forEach(row => {
            if (rex.test(row.id)) {
                for (let key in socialChannels) {
                    if (new RegExp(`.*(${socialChannels[key].join('|')}).*referral$`, 'i').test(row.id)) {
                        row.key = key

                        if (!(key in smm_data)) {
                            smm_data[key] = {key, sessions: 0, goals: 0, daily: []}
                        }
                        smm_data[key].sessions += row.sessions
                        smm_data[key].goals += row.goals
                        smm_data[key].daily = [...smm_data[key].daily, ...row.daily]

                        sesFromSMM += row.sessions
                        goalsFromSMM += row.goals
                    }
                }
            }
        })

        const lastDataItem = mainMetrics.data[mainMetrics.data.length - 1]

        
        lifetimeFans += (lastDataItem?.igFollowers && lastDataItem.igFollowers?.reports?.length > 0 && lastDataItem.igFollowers?.reports[0].data.totals ? lastDataItem.igFollowers?.reports[0].data.totals[0].followers_count : 0)
        lifetimeFans += (lastDataItem?.linkedinOrgLt ? lastDataItem?.linkedinOrgLt?.followerLifetime : lastDataItem?.linkedinOrg ? lastDataItem?.linkedinOrg?.followerLifetime : 0)
        
        const pageFans = mainMetrics?.fbFanMetricsGrouped["page_fans"]

        if(pageFans && pageFans.length > 0){
            lifetimeFans += pageFans[pageFans.length - 1][1]
        }

        newFans += (mainMetrics?.fbFanMetricsGrouped["page_fan_adds_total"] || 0)
        newFans += (mainMetrics?.igMetricsGrouped["follower_count_total"] || 0)

        if(mainMetrics?.linkedInOrgStats){
            newFans += mainMetrics.linkedInOrgStats?.followers
        }

        const mediaData = Object.keys(smm_data).reduce((prev, curr) => {
            if(prev[curr] === undefined){
                prev[curr] = {
                    ...smm_data[curr],
                    plottedItems: {}
                }
            }

            smm_data[curr].daily.forEach(day => {
                const splitDt = day[0].split("-")
                let dt = splitDt[1] + "-" + splitDt[2]
                if(prev.plottedItems[dt] === undefined){
                    prev.plottedItems[dt] = 0
                }
                if(prev[curr].plottedItems[dt] === undefined){
                    prev[curr].plottedItems[dt] = 0
                }

                prev.plottedItems[dt] += day[1]
                prev[curr].plottedItems[dt] += day[1]
            })

            return prev
        }, {
            plottedItems: {}
        })

        
        // if((parseInt(model.period) + 1) < model.periods.length){
            
            let prevtotalAdsSpend = 0;
            
            if(prevStorage.adsByCompaignTotal.length > 0){
                prevtotalAdsSpend += prevStorage.adsByCompaignTotal[3]
            }
            if(prevStorage.adsFBByCompaignTotal.length > 0){
                prevtotalAdsSpend += prevStorage.adsFBByCompaignTotal[3]
            }
            if(prevStorage.bingCampaignMetricsTotals.length > 0){
                prevtotalAdsSpend += prevStorage.bingCampaignMetricsTotals[5]
            }

            prevtotalAdsSpend += prevStorage.linkedInAdSpend

            sessionKpi.prevValue = prevStorage.totalSessions || 0
            
            // sessionKpi.secondaryStats[0].prevValue = prevStorage.goals
            // sessionKpi.secondaryStats[1].prevValue = prevStorage.goalConversion
            
            let totalPrevRevenue = prevEcomMetrics.gaEcomRevenue || 0
            let prevTransactions = prevEcomMetrics.gaEcomTransactions || 0
            
            if(prevStorage?.adsFBByCompaignTotal?.length > 0){        
                totalPrevRevenue += (prevStorage?.adsFBByCompaignTotal[8] || 0)
                prevTransactions += (prevStorage?.adsFBByCompaignTotal[9] || 0)
            }
            
            revenueKpi.prevValue = totalPrevRevenue
            revenueKpi.secondaryStats[0].prevValue = totalPrevRevenue > 0 && prevTransactions > 0 ? totalPrevRevenue / prevTransactions : 0
            revenueKpi.secondaryStats[1].prevValue = prevTransactions
            billboardKpiAds.prevValue = parseFloat(formatAverage(Math.round(prevtotalAdsSpend), 1)||0)
    
            // let prevTotalAdsGCR = 0
            // let prevTotalAdsGoals = 0
            
            // if(prevStorage && Object.keys(prevStorage).length > 0){
            //     prevTotalAdsGCR = formatPercent((prevStorage.adsByCompaignTotal[5] || 0)+
            //         (prevStorage.adsFBByCompaignTotal[5] || 0)+
            //         (prevStorage.bingCampaignMetricsTotals[7] || 0) + 
            //         prevStorage.linkedInGoals, ((prevStorage.adsByCompaignTotal[0] || 0)+
            //         (prevStorage.adsFBByCompaignTotal[0] || 0) + 
            //         (prevStorage.bingCampaignMetricsTotals[4] || 0) +
            //         prevStorage.linkedInClicks
            //     ) ||1, true)
    
            //     prevTotalAdsGoals = (prevStorage.adsByCompaignTotal[5] || 0)+ 
            //         (prevStorage.adsFBByCompaignTotal[5] || 0)+ 
            //         (prevStorage.bingCampaignMetricsTotals[7] || 0) +
            //         prevStorage.linkedInGoals
                
            // }
    
            // billboardKpiAds.secondaryStats[0].prevValue = prevTotalAdsGoals
            // billboardKpiAds.secondaryStats[1].prevValue = prevTotalAdsGCR

            prevStorage?.sesBySrcMediumDetails.forEach(row => {
                if (rex.test(row.id)) {
                    for (let key in socialChannels) {
                        if (new RegExp(`.*(${socialChannels[key].join('|')}).*referral$`, 'i').test(row.id)) {
                            sesFromSMMOld += row.sessions
                        }
                    }
                }
            })
        // }

        let socSessionsKpi = { title: "SOCIAL SESSIONS", value: sesFromSMM || 0, prevValue: sesFromSMMOld || 0, 
            visualComponent: <Link className="btn btn-sm btn-info float-right" to="/channel-performance/SOCIAL">More</Link>,
            secondaryStats: [
                { title: "Lifetime Fans", value: lifetimeFans || 0, color: "bg-teal" },
                { title: "New Fans", value: newFans || 0, color: "bg-blue" },
            ], plottedItems: [{
                data: Object.keys(mediaData.plottedItems).sort().map(x => ({ x, y: mediaData.plottedItems[x]}))
            }], description: "A session is defined as a group of interactions one user takes within a 30 minute time frame on your website." 
        }

        const goalsReferrals = mainMetrics?.sesByMediumTblDetails.find(src => src.id.includes('referral'))

        const radarMetricsByChannel = [
            {label: site?.attr?.isFranchise ? "Brand Referrals" : "Google Business Profile", goals: site?.attr?.isFranchise ? brandReferrals.leads : mainMetrics?.gmbAdMetricTotals.goals, revenue: totalRevenueGMB, color: "#4cd964", revenuePercent: formatPercent(totalRevenueGMB, totalRevenue, true)},
            {label: "Paid Campaigns", goals: totalAdsGoals, revenue: paidAdsRevenue, color: "#ff9500", revenuePercent: paidAdsRevenue > 0 && totalRevenue > 0 ? formatPercent(paidAdsRevenue, totalRevenue, true) : 0},
            {label: site?.attr?.isFranchise ? "Referrals & Other Campaigns" : "SMS Text + Other Campaigns", goals: (mainMetrics?.sesByOtherAdTotals[2] || 0) + (site?.attr?.isFranchise ? (goalsReferrals?.goals ?? 0) : 0) - (site?.attr?.isFranchise ? brandReferrals.leads : 0), revenue: ecomMetrics?.gaOtherEcomRevenue, color: "#ffc107", revenuePercent: ecomMetrics?.gaOtherEcomRevenue > 0 && totalRevenueGA > 0 ? formatPercent(ecomMetrics?.gaOtherEcomRevenue, totalRevenueGA, true) : 0},
            {label: "Email", goals: mainMetrics?.emailCampaignTotals[2] || 0, revenue: emailRevenue, color: "#007aff", revenuePercent: emailRevenue > 0 && totalRevenueGA ? formatPercent(emailRevenue, totalRevenueGA, true) : 0},
            {label: "Organic Search", goals: mainMetrics?.sesBySrcMediumOrganicSum ? mainMetrics?.sesBySrcMediumOrganicSum["goals"] : 0, revenue: ecomMetrics?.gaEcomMetricOrganicSum?.revenue, color: "#ff3b30", revenuePercent: ecomMetrics?.gaEcomMetricOrganicSum?.revenue > 0 && totalRevenue ? formatPercent(ecomMetrics?.gaEcomMetricOrganicSum?.revenue, totalRevenue, true) : 0},
            {
                label: site?.attr?.isFranchise ? "Social Media" : "Social + Referrals", goals: goalsFromSMM + (site?.attr?.isFranchise ? 0 : (goalsReferrals?.goals ?? 0)), 
                revenue: socialRevenue + (ecomMetrics?.gaEcomMetricByMedium?.referral?.revenue ?? 0), color: "#5ac8fa",
                revenuePercent: (socialRevenue + (ecomMetrics?.gaEcomMetricByMedium?.referral?.revenue ?? 0)) > 0 && 
                    totalRevenue > 0 ? 
                    formatPercent(socialRevenue + (ecomMetrics?.gaEcomMetricByMedium?.referral?.revenue ?? 0), totalRevenue, true) : 0
            },
        ].sort((a, b) => {
            if(!site.attr?.isEcommerce){
                return a.goals > b.goals ? -1 : a.goals < b.goals ? 1 : 0
            }
            return a.revenue > b.revenue ? -1 : a.revenue < b.revenue ? 1 : 0
        })

        const cityDictionary = {
            ...state.cityDictionary
        }

        let cityMetrics = []

        // Code below contains the logic to pin point impressions by city on a map

        if(currMetrics?.cityMetrics?.length > 0){
            currMetrics?.cityMetrics?.filter(c => site?.attr?.isEcommerce ? c.transactions > 0 : c.goals > 0).forEach(c => {
                cityMetrics.push({
                    ...c,
                    value: site?.attr?.isEcommerce ? c.transactions : c.goals
                })
            })
        }

        setState(prevState => ({
            ...prevState,
            metricsLoaded: true,
            metricsLoading: false,
            mainMetrics,
            cityDictionary,
            cityMarkers: [],
            cityMetrics,
            hasPrevTrafPerf,
            kpiItems,
            kpiItemsFb,
            sessionKpi,
            revenueKpi,
            billboardKpiAds,
            prevStorage,
            prevPageMetrics,
            pageMetrics,
            radarMetricsByChannel,
            socSessionsKpi,
            recommendations,
            monthMetrics,
            thisMonthEcom,
            metricsThisMonth,
            lastMonthsAudit
        }))
    }

    const topCitiesByValue = () => {
        const { cityMarkers } = state
        const site = model.sites[model.siteActiveIdx] ? model.sites[model.siteActiveIdx] : {}

        const topCities = [...cityMarkers.sort((a, b) => b.value - a.value).slice(0, 5)]

        return <table className="table table-striped">
            <thead>
                <th>City, State</th>
                <th>Sessions</th>
                <th>{site?.attr?.isEcommerce ? "Transactions" : "Conversions"}</th>
            </thead>
            <tbody>
                {topCities.map(city => <tr>
                    <td>{city.name}</td>
                    <td>{city.sessions}</td>
                    <td>{city.value}</td>
                </tr>)}
            </tbody>
        </table>
    }

    const popId = "mapInfo"
    
    const { 
        introJsProcess, 
        prevPageMetrics, 
        pageMetrics, 
        metricsLoaded, 
        metricsLoading,
        sessionKpi, 
        revenueKpi,
        billboardKpiAds, 
        hasPrevTrafPerf, 
        radarMetricsByChannel,
        socSessionsKpi,
        recommendations,
        monthMetrics,
        thisMonthEcom,
        metricsThisMonth,
        lastMonthsAudit,
        cityMetrics,
        popoverOpen
    } = state
    
    const site = model?.sites?.length > 0 ? model.sites[model.siteActiveIdx] ? model.sites[model.siteActiveIdx] : model.sites[0] : {}
    
    if(introJsProcess != null){
        introJsProcess.onexit(onIntoJsExit(true))
        introJsProcess.oncomplete(onIntoJsExit)
    }   
    
    let auditScore = 0
    let baselineScore = 0

    if(lastMonthsAudit && Object.keys(lastMonthsAudit).length > 0){
        auditScore = getScoreStatsForAudit(lastMonthsAudit, "prevMetrics", false)
        baselineScore = getScoreStatsForAudit(lastMonthsAudit)
    }

    return <>
        <h1 className="page-header">Overview <i className="fas fa-question-circle" style={{fontSize: "0.8em"}} onClick={() => runIntroTutorial(0)}></i></h1>
        <HierarchySelection onSelectionBegin={() => setState(prevState => ({...prevState, cityMetrics: [], metricsLoading: true, metricsLoaded: false}))} 
            overrideOnSelection={(dataPath, token) => onSelection(dataPath, token)} 
            onSelectionComplete={(results) => onSelectionComplete(results)} fixedPeriodTop />
        {(metricsLoaded || metricsLoading) && <div>
            <div className="row">
                <div className="col-xl-6 col-lg-6 col-md-12">
                    {metricsLoaded ? <TaskListCard recommendations={recommendations} /> : <SkeletonTaskList />}
                    <div className="row">
                        <div className="col-xl-6 col-lg-12">                        
                            {metricsLoaded ? site?.attr?.isEcommerce ? <KpiItem {...revenueKpi} id="revenueKpiCard" /> : <KpiItem {...sessionKpi} id="sessionKpiCard" /> : <SkeletonKpi />}
                        </div>
                        <div className="col-xl-6 col-lg-12">
                            {metricsLoaded ? <KpiItem id="adSpendKpiCard" {...billboardKpiAds} /> : <SkeletonKpi />}
                        </div>
                    </div>
                    {site?.attr?.isEcommerce ? metricsLoaded ? <RevenueTrackerCard results={monthMetrics} thisMonthEcom={thisMonthEcom} metricsThisMonth={metricsThisMonth} site={site} /> : <SkeletonRevenueTracker /> : <></>}
                    <div className="panel panel-inverse" id="coverYourSix">
                        <div className="panel-heading">
                            <div className="panel-heading-btn">
                                <a href="javascript:;" className="btn btn-xs btn-icon btn-circle btn-default" data-click="panel-expand"><i className="fa fa-expand"></i></a>
                                <a href="javascript:;" className="btn btn-xs btn-icon btn-circle btn-success" data-click="panel-reload"><i className="fa fa-redo"></i></a>
                                <a href="javascript:;" className="btn btn-xs btn-icon btn-circle btn-warning" data-click="panel-collapse"><i className="fa fa-minus"></i></a>
                            </div>
                            <h4 className="panel-title">
                                {site?.attr?.isEcommerce ? "Revenue" : "Goals"} by Channel (top six)
                            </h4>
                        </div>
                        <div className="panel-body" style={{padding: "0px"}}>
                            <div class="card">
                                <div class="card-body">
                                    <div class="d-flex flex-row-reverse bd-highlight mb-2">
                                        <Link id="scoreCardBtn" to="/score-card" className="btn btn-info btn-sm">View Score Card</Link>
                                    </div>
                                    <div className={`row`}>
                                        <div className="col-xl-6 col-lg-12 d-flex justify-content-center align-self-start flex-column">
                                            <h4 class="align-self-center">Cover Your Six Radar Report</h4>
                                            <div className="align-self-center mb-md-4 mb-sm-4 mb-4">
                                                {metricsLoaded ? <ApexChart {...polarAreaGraph} 
                                                    options={{     
                                                        ...polarAreaGraph.options,
                                                        colors: radarMetricsByChannel.map(t => t.color),
                                                        labels: radarMetricsByChannel.map(t => t.label),
                                                        legend: {
                                                            labels: {
                                                                colors: radarMetricsByChannel.map(t => t.color),
                                                                useSeriesColors: false
                                                            },
                                                            markers: {
                                                                fillColors: radarMetricsByChannel.map(t => t.color)
                                                            },
                                                            position: 'bottom'
                                                        },
                                                        tooltip: {
                                                            fillSeriesColor: radarMetricsByChannel.map(t => t.color),
                                                            y: {
                                                                formatter: function(value, { series, seriesIndex, dataPointIndex, w }) {
                                                                    return (site.attr?.isEcommerce ? "$" : "") + parseInt(value).toLocaleString()
                                                                }
                                                            }
                                                        },
                                                        yaxis: {
                                                            style: {
                                                                colors: radarMetricsByChannel.map(t => t.color)
                                                            }
                                                        },
                                                        fill: {
                                                            colors: radarMetricsByChannel.map(t => t.color),
                                                            opacity: 0.9
                                                        }
                                                    }}
                                                    series= {radarMetricsByChannel.map(t => t[site.attr?.isEcommerce ? "revenue" : "goals"].toFixed(0))} />
                                                : <SkeletonPieChart legend="bottom" />}
                                            </div>
                                        </div>
                                        <div className={`col-xl-6 col-lg-12 ${metricsLoading ? "align-self-center flex-column" : ""}`}>
                                            {metricsLoaded ? radarMetricsByChannel.map(type => <>
                                                <h5 className="float-left text-left font-weight-bold">
                                                    {type.label}<br />
                                                    {site.attr?.isEcommerce ? "$" : ""}{type[site.attr?.isEcommerce ? "revenue" : "goals"].toFixed(0)}
                                                </h5>
                                                
                                                {site?.attr?.isEcommerce && <h5 className="float-right text-right text-muted mb-0">
                                                    {type.revenuePercent}%
                                                </h5>}
                                                <div className="clearfix"></div>

                                                <div class="progress">
                                                    <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" aria-valuenow={site?.attr?.isEcommerce ? type.revenuePercent : type.goals} aria-valuemin="0" aria-valuemax="100" style={{backgroundColor: type.color, width: `${site?.attr?.isEcommerce ? type.revenuePercent : type.goals}%`}}></div>
                                                </div><br />
                                            </>) : <Skeleton className="mb-4" count={6} height={60} />}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xl-6 col-lg-6 col-md-12">
                    <div className="row">
                        {site?.attr?.auditBaseline ? <>
                            <div className="col-xl-6 col-lg-12" style={{width:"100%"}}>
                                {metricsLoaded ? <AuditScoreKpi title="AUDIT SCORE" 
                                    score={auditScore} 
                                    baselineScore={baselineScore} 
                                    site={site} 
                                    audit={lastMonthsAudit}
                                     /> : <SkeletonAuditScore /> }
                            </div>
                            <div className="col-xl-6 col-lg-12">
                                {metricsLoaded ? <WebsiteScoreKpi type="MOBILE" 
                                    abbreviate
                                    pageMetrics={pageMetrics} 
                                    prevMetrics={prevPageMetrics} 
                                    visualComponent={<Link className="btn btn-sm btn-info float-right"
                                    to="/pagespeed/MOBILE">More</Link>} /> : <SkeletonKpi size="small" />}
                            
                                {metricsLoaded ? <WebsiteScoreKpi type="DESKTOP" 
                                    abbreviate
                                    pageMetrics={pageMetrics} 
                                    prevMetrics={prevPageMetrics}
                                    visualComponent={<Link className="btn btn-sm btn-info float-right"
                                    to="/pagespeed/DESKTOP">More</Link>} /> : <SkeletonKpi size="small" />}
                            </div>
                        </> : <>
                            <div className="col-xl-6 col-lg-12">
                                {metricsLoaded ? <WebsiteScoreKpi type="MOBILE" 
                                    abbreviate
                                    pageMetrics={pageMetrics} 
                                    prevMetrics={prevPageMetrics} 
                                    visualComponent={<Link className="btn btn-sm btn-info float-right"
                                    to="/pagespeed/MOBILE">More</Link>} /> : <SkeletonKpi size="small" />}
                            </div>
                            <div className="col-xl-6 col-lg-12">
                                {metricsLoaded ? <WebsiteScoreKpi type="DESKTOP" 
                                    abbreviate
                                    pageMetrics={pageMetrics} 
                                    prevMetrics={prevPageMetrics}
                                    visualComponent={<Link className="btn btn-sm btn-info float-right"
                                    to="/pagespeed/DESKTOP">More</Link>} /> : <SkeletonKpi size="small" />}
                            </div>
                        </>}
                    </div>
                    <div className="row">
                        {site?.attr?.isEcommerce && <>
                            <div className="col-xl-6 col-lg-12"> 
                                {metricsLoaded ? <KpiItem {...sessionKpi} id="sessionKpiCard" /> : <SkeletonKpi />}
                            </div>
                            <div className="col-xl-6 col-lg-12"> 
                                {metricsLoaded ? <KpiItem {...socSessionsKpi} /> : <SkeletonKpi />}
                            </div>
                        </>}
                    </div>
                    {!site?.attr?.isEcommerce ? metricsLoaded ? <RevenueTrackerCard results={monthMetrics} thisMonthEcom={thisMonthEcom} metricsThisMonth={metricsThisMonth} site={site} /> : <SkeletonRevenueTracker /> : <></>}
                    <div className="panel panel-inverse" id="cityMap">
                        <div className="panel-heading">
                            <div className="panel-heading-btn">
                                <a href="javascript:;" className="btn btn-xs btn-icon btn-circle btn-default" data-click="panel-expand"><i className="fa fa-expand"></i></a>
                                <a href="javascript:;" className="btn btn-xs btn-icon btn-circle btn-success" data-click="panel-reload"><i className="fa fa-redo"></i></a>
                                <a href="javascript:;" className="btn btn-xs btn-icon btn-circle btn-warning" data-click="panel-collapse"><i className="fa fa-minus"></i></a>
                            </div>
                            <h4 className="panel-title">
                                Map of Conversions By City <span id={popId} className="ml-2" >
                                    <i className="fa fa-info-circle"></i>
                                </span>
                                <Popover trigger="click" placement="top" isOpen={popoverOpen} target={popId} toggle={() => setState(prev => ({
                                    ...prev,
                                    popoverOpen: !popoverOpen
                                }))}>
                                    <PopoverBody>
                                        Not all sessions, conversions, and transactions have an "attached" city for various reasons so this map represents Google Analytics best attempt to attribute metrics with a proper city.
                                    </PopoverBody>
                                </Popover>
                            </h4>
                        </div>
                        <div className="panel-body">
                            <USAMap cityMetrics={cityMetrics} onComplete={(s) => setState(prev => ({
                                ...prev,
                                ...s
                            }))} />
                            <h4>Top 5 Cities</h4>
                            {metricsLoaded ? topCitiesByValue() : <SkeletonTable columns={3} headerRow={<tr>
                                <th>City, State</th>
                                <th>Sessions</th>
                                <th>{site?.attr?.isEcommerce ? "Transactions" : "Conversions"}</th>
                            </tr>} />}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        }
    </>
}

export default Overview